import React from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemText, Toolbar, Box,
  CssBaseline, Typography, Divider, ListItemIcon,
  Avatar
} from '@mui/material';
import {
  AssignmentOutlined,
  SettingsOutlined,
  Logout,
  LocalOfferOutlined,
  EventAvailableOutlined,
  SyncAltOutlined,
  ShoppingCartOutlined,
  QuizOutlined
} from '@mui/icons-material';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import TagList from './Pages/Tags/TagList';
import TaskList from './Pages/Tasks/TaskList';
import AppointmentList from './Pages/Appointments/AppointmentList';
import CalendarCerboSync from './Pages/CalendarSync/CalendarCerboSync';
import AddTagMapping from './Pages/Tags/AddTagMapping';
import Settings from './Pages/Settings';
import Login from './Pages/Login';
import PrivateRoute from './Components/PrivateRoute';
import { AuthProvider, useAuth } from './Context/AuthContext';
import QuestionnaireList from './Pages/Questionnaires/QuestionnaireList';
import OpenOrderList from './Pages/OpenOrders/OpenOrderList';
import NetworkError from './Pages/Networkerror';
import OAuthCallback from './Pages/OAuthCallback';

// Modern dark sidebar palette
const drawerWidth = 220;
const sideMenuColor = "#3D52A0";
const activeMenuColor = "#2b396e";
const menuColor = "#dddddd";
const textColor = "#fff";
const iconColor = "#fff";

const menuOptions = [
  { text: 'Tags', path: '/tags', icon: <LocalOfferOutlined sx={{ fontSize: 17 }} /> },
  { text: 'Tasks', path: '/tasks', icon: <AssignmentOutlined sx={{ fontSize: 17 }} /> },
  { text: 'Appointments', path: '/appointments', icon: <EventAvailableOutlined sx={{ fontSize: 17 }} /> },
  { text: 'Calendar Sync', path: '/calendar-sync', icon: <SyncAltOutlined sx={{ fontSize: 17 }} /> },
  { text: 'Open Orders', path: '/open-orders', icon: <ShoppingCartOutlined sx={{ fontSize: 17 }} /> },
  { text: 'Questionnaires', path: '/questionnaires', icon: <QuizOutlined sx={{ fontSize: 17 }} /> },
];

function SideMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: sideMenuColor,
          color: textColor,
          border: 0,
        },
      }}
    >
        <Divider sx={{ borderColor: "#4565d6", mb: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            p: 1,
            my: 1,
            mx: 2,
            textAlign: 'center',
            bgcolor: activeMenuColor,
            border: 'none',
            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
            borderRadius: 2,
          }}
        >
        {/* Profile Image */}
        <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
          <Typography variant="h6" component="div" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
            {process.env.REACT_APP_CLINIC_NAME || 'Integration'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#4565d6", my: 1 }} />
      <List>
        {menuOptions?.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.8 }}>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  minHeight: 38,
                  px: 2,
                  mx: 2,
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive ? activeMenuColor : 'transparent',
                  color: menuColor,
                  boxShadow: isActive ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                  '&:hover': {
                    backgroundColor: isActive ? activeMenuColor : 'transparent',
                    color: textColor,
                    '& .MuiListItemIcon-root': {
                      color: textColor,
                    },
                  },
                }}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon sx={{ color: textColor, minWidth: 28 }}>
                  {React.cloneElement(item.icon, { fontSize: "small" })}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 13, // reduced from 16
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ borderColor: "#4565d6", mb: 1 }} />
      <List>
        {(() => {
          const isSettingsActive = location.pathname.startsWith('/settings');

          return(
            <ListItemButton
              sx={{
                borderRadius: 2,
                minHeight: 38,
                px: 2,
                mx: 2,
                transition: 'all 0.2s ease',
                backgroundColor: isSettingsActive ? activeMenuColor : 'transparent',
                color: isSettingsActive ? textColor : menuColor,
                '&:hover': {
                  backgroundColor: isSettingsActive ? activeMenuColor : 'transparent',
                  color: textColor,
                  '& .MuiListItemIcon-root': {
                    color: textColor,
                  },
                },
              }}
              onClick={() => navigate('/settings')}
            >
              <ListItemIcon
                sx={{
                  minWidth: 28,
                  color: isSettingsActive ? textColor : menuColor,
                }}
              >
                <SettingsOutlined sx={{ fontSize: 17 }} />
              </ListItemIcon>

              <ListItemText
                primary="Settings"
                primaryTypographyProps={{
                  fontWeight:
                    location.pathname === '/settings' ? 600 : 400,
                  fontSize: 13,
                }}
              />
            </ListItemButton>
          );
        }) ()}

        <ListItem disablePadding>
          <ListItemButton
            sx={{
              borderRadius: 2,
              minHeight: 38,
              px: 2,
              mx: 2,
              transition: 'all 0.2s ease',
              color: menuColor,
              '&:hover': {
                backgroundColor: 'transparent',
                color: textColor,
                '& .MuiListItemIcon-root': {
                  color: textColor,
                },
              },
            }}
            onClick={handleLogout}
          >
            <ListItemIcon sx={{ color: menuColor, minWidth: 28, }}>
              <Logout sx={{ fontSize: 17 }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: 'normal',
                fontSize: 13,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}

function MainContent() {
  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        // The app shell is height:100vh/overflow:hidden, so the main content area must scroll itself —
        // otherwise any page taller than the viewport (e.g. Settings) is clipped and its bottom buttons
        // become unreachable. overflowY:auto lets every page scroll to the bottom.
        overflowY: 'auto',
        minHeight: 0,
        color: "#23244A",
        m: 2,
      }}
    >

      <Routes>
        <Route path="/" element={<Navigate to="/tags" replace />} />
        <Route path="/tags" element={<PrivateRoute><TagList /></PrivateRoute>} />
        <Route path="/tasks" element={<PrivateRoute><TaskList /></PrivateRoute>} />
        <Route path="/appointments" element={<PrivateRoute><AppointmentList /></PrivateRoute>} />
        <Route path="/calendar-sync" element={<PrivateRoute><CalendarCerboSync /></PrivateRoute>} />
        <Route path="/questionnaires" element={<PrivateRoute><QuestionnaireList /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/open-orders" element={<PrivateRoute><OpenOrderList /></PrivateRoute>} />
        <Route path="/networkError" element={<NetworkError />} />
      </Routes>
    </Box>
  );
}

function AppLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // GHL OAuth returns to /Callback?code=... — the admin is already logged in when they hit Connect,
  // so this must render the exchange handler in the AUTHENTICATED state too (the sidebar routes have
  // no /callback match, which left the page blank and the code un-exchanged). Handle it before the
  // auth branch, full-screen (no sidebar). GHL's redirect_uri uses a capital "C", so match
  // case-insensitively.
  if (location.pathname.toLowerCase() === '/callback') {
    return (
      <Box sx={{ display: 'flex', bgcolor: "#F5F6FA", height: '100vh', overflow: 'hidden' }}>
        <CssBaseline />
        <OAuthCallback />
      </Box>
    );
  }

  // Don't show sidebar on login page or network error page
  if (!isAuthenticated || location.pathname === '/networkError') {
    return (
      <Box sx={{ display: 'flex', bgcolor: "#F5F6FA", height: '100vh', overflow: 'hidden' }}>
        <CssBaseline />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/networkError" element={<NetworkError />} />
          <Route path="/callback" element={<OAuthCallback />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', bgcolor: "#F5F6FA", height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <SideMenu />
      <MainContent />
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}

export default App;